/**
 * The ledger — every transaction, searchable.
 *
 * Filtering is instant and client-side: the snapshot is already in memory, so
 * round-tripping a search would only add latency. Rows are grouped by day, which
 * is how a bank statement reads and how people remember spending.
 */

import { useDeferredValue, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  isUnresolvedPending,
  longDate,
  merchantName,
  formatMoney,
  sumCents,
  type Transaction,
} from '@alohamula/domain';
import { PageHeader } from '../components/AppShell';
import { Button, EmptyState, Section, Input, Select } from '../components/ui';
import { useToast } from '../components/ui/ToastProvider';
import { TransactionRow } from '../components/TransactionRow';
import { CategorizeDialog } from '../components/CategorizeDialog';
import { SearchIcon } from '../components/icons';
import {
  useRestoreTransaction,
  useSetAllocations,
  useTrashTransaction,
} from '../api/queries';
import type { Budget } from '../budget/useBudget';
import './pages.css';

type Filter = 'all' | 'period' | 'uncategorized' | 'filed' | 'pending' | 'moneyIn';

const FILTER_LABELS: Record<Filter, string> = {
  period: 'This paycheck',
  filed: 'Categorized',
  all: 'Everything',
  uncategorized: 'Needs a category',
  pending: 'Pending',
  moneyIn: 'Money in',
};

function isFilter(value: string | null): value is Filter {
  return value != null && value in FILTER_LABELS;
}

interface TransactionsPageProps {
  budget: Budget;
  now: Date;
}

export function TransactionsPage({ budget, now }: TransactionsPageProps) {
  const [params, setParams] = useSearchParams();
  const rawFilter = params.get('filter');
  const filter: Filter = isFilter(rawFilter) ? rawFilter : 'period';
  const itemFilter = params.get('item') ?? 'all';
  const monthFilter = params.get('month') ?? 'all';
  const [search, setSearch] = useState('');
  // Typing stays responsive on a few thousand rows.
  const deferredSearch = useDeferredValue(search);
  const [editing, setEditing] = useState<Transaction | null>(null);
  const filedIds = useMemo(
    () => new Set(budget.snapshot.allocations.map((allocation) => allocation.transactionId)),
    [budget.snapshot.allocations]
  );

  const setAllocations = useSetAllocations();
  const trash = useTrashTransaction();
  const restore = useRestoreTransaction();
  const toast = useToast();

  const scoped = useMemo(
    () => scopedTransactions(budget, filter, itemFilter, filedIds),
    [budget, filter, itemFilter, filedIds]
  );
  const months = useMemo(() => monthsIn(scoped), [scoped]);
  const visible = useMemo(() => {
    let rows = scoped;
    if (monthFilter !== 'all') {
      rows = rows.filter((transaction) => transaction.transactionDate.startsWith(monthFilter));
    }
    const needle = deferredSearch.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((transaction) =>
      // Both the cleaned name and the raw bank line, so searching either works.
      `${merchantName(transaction.description)} ${transaction.description}`
        .toLowerCase()
        .includes(needle)
    );
  }, [scoped, monthFilter, deferredSearch]);

  const days = useMemo(() => groupByDay(visible), [visible]);
  const total = spentOn(visible);

  return (
    <>
      {/* The nav calls this place Transactions, so the page has to as well.
          "Ledger" was a second name for one object. */}
      <PageHeader title="Transactions" period={budget.period} />

      <div className="card" style={{ padding: 'var(--space-sm)' }}>
        <div className="toolbar">
          <div className="search">
            <SearchIcon className="search__icon" width={16} height={16} />
            <Input
              type="search"
              value={search}
              placeholder="Search transactions"
              aria-label="Search transactions"
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <Select
            value={filter}
            aria-label="Filter transactions"
            onChange={(event) => {
              const next = new URLSearchParams(params);
              const value = event.target.value;
              next.set('filter', value);
              // An envelope filter has nothing to match against unfiled charges.
              if (value === 'uncategorized') next.delete('item');
              setParams(next, { replace: true });
            }}
          >
            {(Object.keys(FILTER_LABELS) as Filter[]).map((option) => (
              <option key={option} value={option}>
                {FILTER_LABELS[option]}
              </option>
            ))}
          </Select>
          <Select
            value={itemFilter}
            aria-label="Filter by envelope or bill"
            onChange={(event) => {
              const next = new URLSearchParams(params);
              if (event.target.value === 'all') next.delete('item');
              else next.set('item', event.target.value);
              if (filter === 'uncategorized' && event.target.value !== 'all') {
                next.set('filter', 'filed');
              }
              setParams(next, { replace: true });
            }}
          >
            <option value="all">Any envelope</option>
            {budget.activeItems
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </Select>
          {months.length > 0 && (
            <Select
              value={monthFilter}
              aria-label="Filter by month"
              onChange={(event) => {
                const next = new URLSearchParams(params);
                if (event.target.value === 'all') next.delete('month');
                else next.set('month', event.target.value);
                setParams(next, { replace: true });
              }}
            >
              <option value="all">All months</option>
              {months.map((month) => (
                <option key={month.key} value={month.key}>
                  {month.label}
                </option>
              ))}
            </Select>
          )}
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="card">
          <EmptyState
            title={search ? `Nothing matches “${search}”` : 'Nothing here'}
            body={
              search
                ? 'Try part of the merchant name.'
                : monthFilter !== 'all' || itemFilter !== 'all'
                  ? 'Try another month or envelope.'
                  : 'When the scraper finds new transactions they will show up here.'
            }
            action={
              search ? <Button onClick={() => setSearch('')}>Clear search</Button> : undefined
            }
          />
        </div>
      ) : (
        <Section
          title={`${visible.length} ${visible.length === 1 ? 'transaction' : 'transactions'}`}
          meta={`${formatMoney(total)} out`}
        >
          <div className="stack" style={{ gap: 'var(--space-md)' }}>
            {days.map(([date, transactions]) => (
              <div key={date}>
                <p className="day-head">
                  <span>{longDate(date, now)}</span>
                  {/* A day of nothing but refunds has no spending to total, and
                      "$0.00" would read as a day where nothing happened. */}
                  {spentOn(transactions) > 0 && (
                    <span className="numeric">{formatMoney(spentOn(transactions))}</span>
                  )}
                </p>
                <ul className="card tx-list">
                  {transactions.map((transaction) => (
                    <TransactionRow
                      key={transaction.id}
                      transaction={transaction}
                      index={budget.index}
                      itemsById={budget.itemsById}
                      now={now}
                      actions={
                        <>
                          <Button size="sm" onClick={() => setEditing(transaction)}>
                            {budget.index.allocationsByTransaction.has(transaction.id)
                              ? 'Recategorize'
                              : 'Categorize'}
                          </Button>
                          {isUnresolvedPending(transaction, now) && (
                            <Button
                              size="sm"
                              emphasis="ghost"
                              onClick={async () => {
                                const trashed = await trash.mutateAsync({
                                  id: transaction.id,
                                  expectedVersion: transaction.version,
                                  reason: 'Pending hold never posted',
                                });
                                toast.showUndo('Moved to trash', () =>
                                  restore.mutate({
                                    id: transaction.id,
                                    expectedVersion: trashed.version,
                                  })
                                );
                              }}
                            >
                              Never posted
                            </Button>
                          )}
                        </>
                      }
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      <CategorizeDialog
        transaction={editing}
        envelopes={budget.envelopes}
        bills={budget.bills}
        initialSplits={
          editing
            ? budget.index.allocationsByTransaction
                .get(editing.id)
                ?.map((allocation) => ({
                  budgetItemId: allocation.budgetItemId,
                  amount: allocation.amount,
                }))
            : undefined
        }
        busy={setAllocations.isPending}
        onClose={() => setEditing(null)}
        onSubmit={async (allocations) => {
          const transaction = editing;
          if (!transaction) return;
          setEditing(null);
          const result = await setAllocations.mutateAsync({
            transactionId: transaction.id,
            allocations,
            expectedVersion: transaction.version,
          });
          const previous =
            budget.index.allocationsByTransaction.get(transaction.id)?.map((allocation) => ({
              budgetItemId: allocation.budgetItemId,
              amount: allocation.amount,
            })) ?? [];
          toast.showUndo('Recategorized', () => {
            setAllocations.mutate({
              transactionId: transaction.id,
              allocations: previous,
              expectedVersion: result.transactionVersion,
            });
          });
        }}
      />
    </>
  );
}

/**
 * Money out of a set of rows. Not `moneySpent`, which excludes transfers because
 * they are not spending — here the point is what left the account.
 */
function spentOn(transactions: readonly Transaction[]): number {
  return sumCents(
    transactions.filter((t) => t.amount < 0).map((t) => Math.abs(t.amount))
  );
}

function scopedTransactions(
  budget: Budget,
  filter: Filter,
  itemFilter: string,
  filedIds: Set<string>
): Transaction[] {
  let rows =
    filter === 'period'
      ? budget.transactionsInPeriod
      : filter === 'uncategorized'
        ? budget.uncategorized
        : filter === 'filed'
          ? budget.transactions.filter((transaction) => filedIds.has(transaction.id))
          : filter === 'pending'
            ? budget.transactions.filter((transaction) => transaction.status === 'pending')
            : filter === 'moneyIn'
              ? budget.transactions.filter((transaction) => transaction.amount > 0)
              : budget.transactions;

  if (itemFilter !== 'all' && filter !== 'uncategorized') {
    const ids = new Set(
      budget.snapshot.allocations
        .filter((allocation) => allocation.budgetItemId === itemFilter)
        .map((allocation) => allocation.transactionId)
    );
    rows = rows.filter((transaction) => ids.has(transaction.id));
  }
  return [...rows];
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function monthsIn(transactions: readonly Transaction[]): { key: string; label: string }[] {
  const seen = new Set<string>();
  const months: { key: string; label: string }[] = [];
  for (const transaction of transactions) {
    const key = transaction.transactionDate.slice(0, 7);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const month = Number(key.slice(5, 7));
    const year = key.slice(0, 4);
    months.push({
      key,
      label: MONTH_NAMES[month - 1] ? `${MONTH_NAMES[month - 1]} ${year}` : key,
    });
  }
  return months.sort((a, b) => b.key.localeCompare(a.key));
}

/** Newest day first. `transactions` is already sorted, so groups keep their order. */
function groupByDay(transactions: readonly Transaction[]): [string, Transaction[]][] {
  const days = new Map<string, Transaction[]>();
  for (const transaction of transactions) {
    const day = transaction.transactionDate.slice(0, 10);
    const bucket = days.get(day);
    if (bucket) bucket.push(transaction);
    else days.set(day, [transaction]);
  }
  return [...days.entries()].sort((a, b) => b[0].localeCompare(a[0]));
}
